import { ConditionOperatorEnumType, MAX_CONDITION_GROUP_DEPTH } from '@reach/shared-types/conditions';
import { generateId } from '../utils/generate-id';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { AddRelatedResourceCondition } from './AddRelatedResourceCondition';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { LogicPillToggle } from './LogicPillToggle';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import { buildAddAttributeLabel } from './segment-builder.utils';
import { useSegmentSchemaCatalog } from './SegmentSchemaCatalog';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { useClauseDescriptionVisible, useSegmentReadOnly } from './segment-viewer-settings-context';

interface NestedGroupBlockProps {
  condition: BuilderNestedGroup;
  onUpdate: (condition: BuilderNestedGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
  depth: number;
  /** Inside a related-record block or sub group, below the condition group's direct clauses. */
  nested?: boolean;
}

export const NestedGroupBlock: React.FC<NestedGroupBlockProps> = ({
  condition,
  onUpdate,
  onDelete,
  disabled = false,
  depth,
  nested = false,
}) => {
  const readOnly = useSegmentReadOnly();
  const descriptionVisible = useClauseDescriptionVisible(nested);
  const canAddDescription = descriptionVisible && !readOnly && condition.description === undefined;
  const visibleDescription = descriptionVisible ? condition.description : undefined;
  const { schemaCatalog, getSchemaById } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const { contactFields } = schemaCatalog;
  const canNestDeeper = depth < MAX_CONDITION_GROUP_DEPTH;
  const contactName = schemaCatalog.contactSchemaId
    ? (getSchemaById(schemaCatalog.contactSchemaId)?.name ?? 'Contact')
    : 'Contact';
  const addContactConditionLabel = buildAddAttributeLabel(contactName);
  const contactSubject = {
    schemaId: schemaCatalog.contactSchemaId ?? undefined,
    name: contactName,
    displayMetadata: schemaCatalog.contactSchemaId
      ? getSchemaById(schemaCatalog.contactSchemaId)?.displayMetadata
      : undefined,
  };
  const isExpanded = condition.isExpanded;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleLogicChange = (logic: 'and' | 'or') => {
    onUpdate({ ...condition, logic });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  const handleConditionUpdate = (index: number, cond: BuilderCondition) => {
    onUpdate({ ...condition, conditions: condition.conditions.with(index, cond) });
  };

  const handleConditionDelete = (index: number) => {
    onUpdate({ ...condition, conditions: condition.conditions.toSpliced(index, 1) });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for clarity
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: condition.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newNestedGroup],
    });
  };

  const handleAddRelatedResource = (newCond: BuilderRelatedResourceCondition) => {
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  return (
    <div className={styles.nestedGroupBlock}>
      <div className={styles.nestedGroupHeader}>
        <div className={styles.nestedGroupTitle}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={disabled}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <span>Sub Group</span>
        </div>
        <div className={styles.nodeHeaderControls}>
          {canAddDescription && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          {!readOnly && (
            <button
              type="button"
              className={styles.relatedTitleButton}
              onClick={onDelete}
              disabled={disabled}
              aria-label="Remove nested group">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {visibleDescription !== undefined && (
        <div className={styles.nestedGroupDescription}>
          <ClauseDescriptionHeadline
            variant="group"
            description={visibleDescription}
            onChange={handleDescriptionChange}
            disabled={disabled}
          />
        </div>
      )}

      {isExpanded && (
        <div className={styles.nestedGroupContent}>
          {condition.conditions.map((cond, i) => (
            <React.Fragment key={cond.id}>
              {i > 0 && (
                <div className={styles.andConnector}>
                  <LogicPillToggle
                    value={condition.logic}
                    onChange={handleLogicChange}
                    disabled={disabled}
                    ariaLabel="Nested group logic"
                  />
                  <span className={styles.andConnectorLine} />
                </div>
              )}
              {isBuilderFieldCondition(cond) ? (
                <FieldConditionRow
                  condition={cond}
                  fieldOptions={contactFields}
                  subject={contactSubject}
                  onUpdate={handleConditionUpdate.bind(null, i)}
                  onDelete={handleConditionDelete.bind(null, i)}
                  disabled={disabled}
                  nested
                />
              ) : isBuilderRelatedResourceCondition(cond) ? (
                <RelatedResourceBlock
                  condition={cond}
                  subject={contactSubject}
                  subjectSchemaId={schemaCatalog.contactSchemaId}
                  onUpdate={handleConditionUpdate.bind(null, i)}
                  onDelete={handleConditionDelete.bind(null, i)}
                  disabled={disabled}
                  nested
                />
              ) : isBuilderNestedGroup(cond) ? (
                <NestedGroupBlock
                  condition={cond}
                  onUpdate={handleConditionUpdate.bind(null, i)}
                  onDelete={handleConditionDelete.bind(null, i)}
                  disabled={disabled}
                  depth={depth + 1}
                  nested
                />
              ) : null}
            </React.Fragment>
          ))}

          {!readOnly && (
            <div className={styles.nestedGroupAddRow}>
              <button
                type="button"
                className={styles.addConditionBtn}
                onClick={handleAddFieldCondition}
                disabled={disabled || contactFields.length === 0}>
                <Plus size={14} />
                {addContactConditionLabel}
              </button>
              {canNestDeeper && (
                <button
                  type="button"
                  className={styles.addConditionBtn}
                  onClick={handleAddNestedGroup}
                  disabled={disabled}>
                  <Plus size={14} />
                  Sub Grouping
                </button>
              )}
              <AddRelatedResourceCondition
                subjectSchemaId={schemaCatalog.contactSchemaId}
                addRelatedResourceCondition={handleAddRelatedResource}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
